import { currencyFormatter, formatChannelLabel } from "../../../lib/presentation";
import type { HistoryIntervalRow, HistoryYearlyStat, TransportHistoryRow } from "./types";
import { formatDate, formatDaysInterval } from "./utils";

type MatrixRow = string[];

function escapeHtml(value: string) {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

function renderMatrixTable(title: string, headers: string[], rows: MatrixRow[]) {
    return `
        <section style="margin-top: 24px;">
            <h2 style="font-size: 16px; margin: 0 0 10px; color: #0f172a;">${escapeHtml(title)}</h2>
            <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
                <thead>
                    <tr>
                        ${headers.map((header) => `<th style="padding: 8px; border: 1px solid #d8e1eb; background: #f8fafc; text-align: left;">${escapeHtml(header)}</th>`).join("")}
                    </tr>
                </thead>
                <tbody>
                    ${rows.map((row) => `
                        <tr>
                            ${row.map((value) => `<td style="padding: 8px; border: 1px solid #d8e1eb; vertical-align: top;">${escapeHtml(value || "—")}</td>`).join("")}
                        </tr>
                    `).join("")}
                </tbody>
            </table>
        </section>
    `;
}

function openPdfDocument(title: string, subtitle: string, sections: string[]) {
    const html = `
        <!doctype html>
        <html lang="bg">
            <head>
                <meta charset="utf-8" />
                <title>${escapeHtml(title)}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 28px; color: #0f172a; }
                    h1 { margin: 0 0 8px; font-size: 24px; }
                    p { margin: 0; color: #475569; line-height: 1.55; }
                    .actions { margin-top: 18px; display: flex; gap: 10px; }
                    button { border: 1px solid #cbd5e1; background: white; color: #0f172a; border-radius: 999px; padding: 8px 14px; cursor: pointer; }
                    @media print { .actions { display: none; } }
                </style>
            </head>
            <body>
                <h1>${escapeHtml(title)}</h1>
                <p>${escapeHtml(subtitle)}</p>
                ${sections.join("")}
                <div class="actions">
                    <button onclick="window.print()">Печат / Save as PDF</button>
                    <button onclick="window.close()">Затвори</button>
                </div>
            </body>
        </html>
    `;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);
    const printWindow = window.open(blobUrl, "_blank");
    if (!printWindow) {
        URL.revokeObjectURL(blobUrl);
        return;
    }

    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

export function exportDocumentHistoryPdf(intervals: HistoryIntervalRow[]) {
    openPdfDocument(
        "Клиентска история по документи",
        "Подготвен документ за печат или Save as PDF.",
        [
            renderMatrixTable(
                "История по документи",
                ["Дата", "Документ", "Шофьор / Бус", "Канал", "Статус", "Сума", "Интервал"],
                intervals.map(({ order, intervalDays }) => [
                    formatDate(order.createdAt),
                    order.orderNumber,
                    [order.deliveryTransport || order.deliveryDriver || order.deliveryBus || "—", order.deliveryRoute || ""].filter(Boolean).join(" | "),
                    formatChannelLabel(order.channel),
                    order.status,
                    currencyFormatter.format(order.value),
                    formatDaysInterval(intervalDays),
                ]),
            ),
        ],
    );
}

export function exportYearlyStatsPdf(yearlyStats: HistoryYearlyStat[]) {
    openPdfDocument(
        "Клиентски годишен отчет",
        "Годишна справка за печат или Save as PDF.",
        [
            renderMatrixTable(
                "Годишен отчет",
                ["Година", "Документи", "Обща сума", "Средно"],
                yearlyStats.map((row) => [
                    String(row.year),
                    String(row.orders),
                    currencyFormatter.format(row.totalValue),
                    currencyFormatter.format(row.averageValue),
                ]),
            ),
        ],
    );
}

export function exportTransportHistoryPdf(rows: TransportHistoryRow[]) {
    openPdfDocument(
        "Транспортна история",
        "Транспортни редове за печат или Save as PDF.",
        [
            renderMatrixTable(
                "Транспортна история",
                ["Дата", "Клиент", "Локация", "Бус", "Маршрут", "Бележка", "Код"],
                rows.map((row) => [
                    formatDate(row.transportDate),
                    row.clientName || "—",
                    row.location || "—",
                    row.bus || "—",
                    row.route || "—",
                    row.note || "—",
                    row.clientCode || "—",
                ]),
            ),
        ],
    );
}
